import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  clearUserSession,
  getStoredUser,
  getUserProfile,
  getUserProfileSnapshot,
  subscribeToCacheInvalidation,
  subscribeToUserSession,
} from "../services/api";
import prof from "../assets/boy.png";
import { prefetchRoute } from "../routes/routeChunks";

const MARKETING_NAV_ITEMS = [
  {
    label: "Home",
    to: "/",
    isActive: (pathname) => pathname === "/",
  },
  {
    label: "Features",
    to: "/features",
    isActive: (pathname) => pathname === "/features",
  },
  {
    label: "Pricing",
    to: "/pricing",
    isActive: (pathname) => pathname === "/pricing" || pathname === "/subscribe",
  },
  {
    label: "Launchpad",
    to: "/launchpad",
    isActive: (pathname) =>
      pathname === "/launchpad" ||
      pathname === "/career-suite" ||
      pathname === "/ai-resume-builder" ||
      pathname === "/job-application-assistant" ||
      pathname === "/interview-preparation",
  },
];

const resolveAvatar = (value) => {
  if (!value) return null;
  const trimmed = String(value).trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return null;
  return trimmed;
};

const normalizePlanName = (value) => {
  const normalized = String(value || "").toLowerCase();
  if (normalized === "premium") return "pro";
  if (normalized === "basic" || normalized === "pro" || normalized === "free") {
    return normalized;
  }
  return "free";
};

const getInitialUser = () => {
  if (typeof window === "undefined") return null;
  return getStoredUser();
};

const getInitialAvatar = () => {
  if (typeof window === "undefined") return prof;
  const cached = getUserProfileSnapshot({ maxAge: 5 * 60 * 1000 });
  const cachedAvatar = resolveAvatar(cached?.avatar);
  return cachedAvatar || prof;
};

const getInitialPlan = () => {
  if (typeof window === "undefined") return "free";
  const cached = getUserProfileSnapshot({ maxAge: 5 * 60 * 1000 });
  return normalizePlanName(cached?.plan || cached?.subscription?.plan);
};

export default function Header({ showGetStarted }) {
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [user, setUser] = useState(getInitialUser);
  const [avatar, setAvatar] = useState(getInitialAvatar); // default avatar
  const [userPlan, setUserPlan] = useState(getInitialPlan);
  const userIdRef = useRef(getInitialUser()?.id || null);

  const isMarketing = MARKETING_NAV_ITEMS.some((item) => item.isActive(location.pathname));
  const isUserPortal = !location.pathname.startsWith("/u/");
  const showProfileMenu = Boolean(user && isUserPortal);
  const canAccessJobApplications =
    showProfileMenu && ["free", "basic", "pro"].includes(userPlan);
  const handlePrefetch = (path) => () => {
    prefetchRoute(path);
  };

  const handleSignOut = async () => {
    await clearUserSession();
    window.location.href = "/";
  };

  const refreshProfile = useCallback(async (options = {}) => {
    const data = await getUserProfile(options);
    const nextUser = getStoredUser() || null;
    const nextAvatar = resolveAvatar(data?.avatar);
    setUser(nextUser);
    setAvatar(nextAvatar || prof);
    setUserPlan(normalizePlanName(data?.plan || data?.subscription?.plan));
  }, []);

  const syncUserSession = useCallback(
    (options = {}) => {
      const nextUser = getStoredUser();
      const previousUserId = userIdRef.current;
      userIdRef.current = nextUser?.id || null;

      if (!nextUser?.id) {
        setUser(null);
        setAvatar(prof);
        setUserPlan("free");
        return;
      }

      setUser(nextUser);

      const cachedProfile = getUserProfileSnapshot({ maxAge: 5 * 60 * 1000 });
      const cachedAvatar = resolveAvatar(cachedProfile?.avatar);
      const cachedPlan = normalizePlanName(
        cachedProfile?.plan || cachedProfile?.subscription?.plan
      );
      if (cachedAvatar) {
        setAvatar(cachedAvatar);
      }
      setUserPlan(cachedPlan);

      const shouldRefreshProfile = options.forceAvatar || nextUser.id !== previousUserId;
      if (shouldRefreshProfile) {
        refreshProfile({ force: true }).catch(() => {
          if (options.strict) {
            void clearUserSession({ server: false, reason: "expired" });
            setUser(null);
            setAvatar(prof);
            setUserPlan("free");
          }
        });
      }
    },
    [refreshProfile]
  );

  useEffect(() => {
    syncUserSession({ forceAvatar: true, strict: true });
  }, [syncUserSession]);

  useEffect(() => {
    syncUserSession();
  }, [location.pathname, syncUserSession]);

  useEffect(
    () =>
      subscribeToUserSession(() => {
        syncUserSession({ forceAvatar: true });
      }),
    [syncUserSession]
  );

  useEffect(() => {
    return subscribeToCacheInvalidation((payload) => {
      const prefixes = payload?.prefixes;
      const targets = Array.isArray(prefixes) ? prefixes : [prefixes];
      if (!targets || targets.length === 0) return;
      if (
        targets.some(
          (prefix) =>
            prefix === "*" || "/user/me".startsWith(prefix) || prefix.startsWith("/user/me")
        )
      ) {
        refreshProfile({ force: true }).catch(() => {});
      }
    });
  }, [refreshProfile]);

  useEffect(() => {
    setMenuOpen(false);
    setMobileProfileOpen(false);
  }, [location.pathname]);

  return (
    <header className="app-header fixed top-0 left-0 z-[120] w-full border-b border-gray-800 bg-[#171717] text-white">
      <div className="mx-auto flex h-[76px] w-full max-w-7xl items-center justify-between px-4 sm:h-[80px] sm:px-6">
        <Link to="/">
          <div className="flex items-center space-x-2">
            <img src="/logo.png" alt="AetherCV Logo" className="h-11 w-11 sm:h-14 sm:w-14" />
            <h1 className="text-lg font-bold sm:text-xl">AetherCV</h1>
          </div>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden xl:flex items-center gap-6 text-sm">
          {isMarketing && (
            <div className="flex items-center gap-6 2xl:gap-8">
              {MARKETING_NAV_ITEMS.map((item) => {
                const active = item.isActive(location.pathname);
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`whitespace-nowrap transition ${active ? "text-yellow-300" : "text-white hover:text-yellow-400"}`}
                  >
                    {item.label}
                  </Link>
                );
              })}
              {!user && (
                <Link
                  to="/login"
                  onMouseEnter={handlePrefetch("/login")}
                  onFocus={handlePrefetch("/login")}
                  className={`whitespace-nowrap transition ${location.pathname === "/login" ? "text-yellow-300" : "text-white hover:text-yellow-400"}`}
                >
                  Login
                </Link>
              )}
            </div>
          )}

          {showProfileMenu ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="focus:outline-none"
                aria-expanded={menuOpen}
                aria-haspopup="menu"
              >
                <img
                  src={avatar}
                  alt="Profile"
                  className={`h-11 w-11 rounded-full object-cover ring-1 ring-white/20 transition-transform duration-150 ${menuOpen ? "scale-[0.98]" : "scale-100"}`}
                />
              </button>
              {menuOpen && (
                <div className="absolute right-0 mt-2 w-60 rounded-xl border border-gray-700/80 bg-[#171717]/95 p-4 text-sm shadow-2xl backdrop-blur">
                  <div className="space-y-1">
                    <div>
                      <Link
                        to="/dashboard"
                        onClick={() => setMenuOpen(false)}
                        onMouseEnter={handlePrefetch("/dashboard")}
                        onFocus={handlePrefetch("/dashboard")}
                        className="block py-2 hover:text-yellow-400"
                      >
                        Dashboard
                      </Link>
                    </div>
                    <div>
                      <Link
                        to="/my-resumes"
                        onClick={() => setMenuOpen(false)}
                        onMouseEnter={handlePrefetch("/my-resumes")}
                        onFocus={handlePrefetch("/my-resumes")}
                        className="block py-2 hover:text-yellow-400"
                      >
                        My Resumes
                      </Link>
                    </div>
                    <div>
                      <Link
                        to="/interview-plans"
                        onClick={() => setMenuOpen(false)}
                        onMouseEnter={handlePrefetch("/interview-plans")}
                        onFocus={handlePrefetch("/interview-plans")}
                        className="block py-2 hover:text-yellow-400"
                      >
                        Interview Plans
                      </Link>
                    </div>
                    {canAccessJobApplications ? (
                      <div>
                        <Link
                          to="/job-applications"
                          onClick={() => setMenuOpen(false)}
                          onMouseEnter={handlePrefetch("/job-applications")}
                          onFocus={handlePrefetch("/job-applications")}
                          className="block py-2 hover:text-yellow-400"
                        >
                          Job Applications
                        </Link>
                      </div>
                    ) : null}
                    <div>
                      <Link
                        to="/settings"
                        onClick={() => setMenuOpen(false)}
                        onMouseEnter={handlePrefetch("/settings")}
                        onFocus={handlePrefetch("/settings")}
                        className="block py-2 hover:text-yellow-400"
                      >
                        Settings
                      </Link>
                    </div>
                    <div>
                      <Link
                        to="/subscribe"
                        onClick={() => setMenuOpen(false)}
                        onMouseEnter={handlePrefetch("/subscribe")}
                        onFocus={handlePrefetch("/subscribe")}
                        className="block py-2 hover:text-yellow-400"
                      >
                        Subscription
                      </Link>
                    </div>
                    <div>
                      <Link
                        to="/billing"
                        onClick={() => setMenuOpen(false)}
                        onMouseEnter={handlePrefetch("/billing")}
                        onFocus={handlePrefetch("/billing")}
                        className="block py-2 hover:text-yellow-400"
                      >
                        Billing & Invoices
                      </Link>
                    </div>
                    <div>
                      <Link
                        to="/support"
                        onClick={() => setMenuOpen(false)}
                        onMouseEnter={handlePrefetch("/support")}
                        onFocus={handlePrefetch("/support")}
                        className="block py-2 hover:text-yellow-400"
                      >
                        Support
                      </Link>
                    </div>
                    <div>
                      <button
                        type="button"
                        onClick={handleSignOut}
                        className="block w-full text-left py-2 hover:text-red-400"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
          !user &&
          showGetStarted && (
            <Link
              to="/register"
              onMouseEnter={handlePrefetch("/register")}
              onFocus={handlePrefetch("/register")}
              className="whitespace-nowrap rounded-xl bg-yellow-400 px-5 py-3 font-semibold text-black"
            >
              Get Started
            </Link>
          )
        )}
      </nav>

      {/* Mobile Menu Icon */}
      <div className="xl:hidden">
        <button
          onClick={() => {
            setMobileOpen(true);
            setMobileProfileOpen(false);
          }}
          className="focus:outline-none"
        >
          <Menu className="w-6 h-6" />
        </button>
      </div>

      {/* Mobile Fullscreen Overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-[130] flex flex-col justify-between bg-[linear-gradient(180deg,#050505_0%,#090909_55%,#111111_100%)] px-6 py-8"
          role="dialog"
          aria-modal="true"
        >
            {/* Top */}
            <div className="flex justify-between items-center">
              <h1 className="text-xl font-bold">AetherCV AI</h1>
              <button
                onClick={() => {
                  setMobileOpen(false);
                  setMobileProfileOpen(false);
                }}
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Nav Links */}
            <div className="mt-20 flex flex-col gap-6 text-xl font-medium">
              {isMarketing && (
                <>
                  {MARKETING_NAV_ITEMS.map((item) => {
                    const active = item.isActive(location.pathname);
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        onClick={() => setMobileOpen(false)}
                        className={`block w-full rounded-xl px-4 py-3 text-[22px] leading-tight transition ${
                          active
                            ? "bg-[#1a1a1a] text-yellow-200"
                            : "bg-[#111111] text-white hover:bg-[#181818] hover:text-yellow-300"
                        }`}
                      >
                        {item.label}
                      </Link>
                    );
                  })}
                  {!user && (
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      onMouseEnter={handlePrefetch("/login")}
                      onFocus={handlePrefetch("/login")}
                      className="block w-full rounded-xl bg-[#111111] px-4 py-3 text-[22px] leading-tight text-white transition hover:bg-[#181818] hover:text-yellow-300"
                    >
                      Login
                    </Link>
                  )}
                </>
              )}
              {showProfileMenu ? (
                <>
                  <button
                    type="button"
                    onClick={() => setMobileProfileOpen((open) => !open)}
                    className="flex w-full items-center justify-between rounded-xl bg-[#111111] px-4 py-3"
                  >
                    <span className="flex items-center gap-3">
                      <img
                        src={avatar}
                        alt="Profile"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <span>Account</span>
                    </span>
                    <ChevronDown
                      className={`w-5 h-5 transition-transform ${
                        mobileProfileOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {mobileProfileOpen && (
                    <div className="mt-2 overflow-hidden rounded-xl bg-[#0c0c0c] text-base text-gray-200 shadow-[0_10px_35px_rgba(0,0,0,0.38)]">
                        <div className="space-y-1 p-1">
                          <div>
                            <Link
                              to="/dashboard"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                              }}
                              onMouseEnter={handlePrefetch("/dashboard")}
                              onFocus={handlePrefetch("/dashboard")}
                              className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                            >
                              Dashboard
                            </Link>
                          </div>
                          <div>
                            <Link
                              to="/my-resumes"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                              }}
                              onMouseEnter={handlePrefetch("/my-resumes")}
                              onFocus={handlePrefetch("/my-resumes")}
                              className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                            >
                              My Resumes
                            </Link>
                          </div>
                          <div>
                            <Link
                              to="/interview-plans"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                              }}
                              onMouseEnter={handlePrefetch("/interview-plans")}
                              onFocus={handlePrefetch("/interview-plans")}
                              className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                            >
                              Interview Plans
                            </Link>
                          </div>
                          {canAccessJobApplications ? (
                            <div>
                              <Link
                                to="/job-applications"
                                onClick={() => {
                                  setMobileOpen(false);
                                  setMobileProfileOpen(false);
                                }}
                                onMouseEnter={handlePrefetch("/job-applications")}
                                onFocus={handlePrefetch("/job-applications")}
                                className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                              >
                                Job Applications
                              </Link>
                            </div>
                          ) : null}
                          <div>
                            <Link
                              to="/settings"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                              }}
                              onMouseEnter={handlePrefetch("/settings")}
                              onFocus={handlePrefetch("/settings")}
                              className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                            >
                              Settings
                            </Link>
                          </div>
                          <div>
                            <Link
                              to="/subscribe"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                              }}
                              onMouseEnter={handlePrefetch("/subscribe")}
                              onFocus={handlePrefetch("/subscribe")}
                              className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                            >
                              Subscription
                            </Link>
                          </div>
                          <div>
                            <Link
                              to="/billing"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                              }}
                              onMouseEnter={handlePrefetch("/billing")}
                              onFocus={handlePrefetch("/billing")}
                              className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                            >
                              Billing & Invoices
                            </Link>
                          </div>
                          <div>
                            <Link
                              to="/support"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                              }}
                              onMouseEnter={handlePrefetch("/support")}
                              onFocus={handlePrefetch("/support")}
                              className="block w-full rounded-lg px-4 py-3 leading-none transition hover:bg-[#181818] hover:text-yellow-300"
                            >
                              Support
                            </Link>
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() => {
                                setMobileOpen(false);
                                setMobileProfileOpen(false);
                                handleSignOut();
                              }}
                              className="block w-full rounded-lg px-4 py-3 text-left leading-none transition hover:bg-[#181818] hover:text-red-400"
                            >
                              Sign Out
                            </button>
                          </div>
                        </div>
                    </div>
                  )}
                </>
              ) : (
                !user && !isMarketing && (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      onMouseEnter={handlePrefetch("/login")}
                      onFocus={handlePrefetch("/login")}
                      className="block w-full rounded-xl bg-[#111111] px-4 py-3 text-[22px] leading-tight text-white transition hover:bg-[#181818] hover:text-yellow-300"
                    >
                      Login
                    </Link>
                    {showGetStarted && (
                      <Link
                        to="/register"
                        className="rounded-xl bg-yellow-400 px-5 py-3 font-semibold text-black"
                        onClick={() => setMobileOpen(false)}
                        onMouseEnter={handlePrefetch("/register")}
                        onFocus={handlePrefetch("/register")}
                      >
                        Get Started
                      </Link>
                    )}
                  </>
                )
              )}
            </div>

            {/* Footer */}
            <div className="text-gray-400 text-xs text-center mt-auto">
              © {new Date().getFullYear()} AetherCV AI
            </div>
        </div>
      )}
      </div>
    </header>
  );
}
